import * as i0 from '@angular/core';
import { EventEmitter, inject, ChangeDetectorRef, Component, ChangeDetectionStrategy, Input, Output, ViewChild, HostBinding, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { NG_VALUE_ACCESSOR, NG_ASYNC_VALIDATORS, FormsModule } from '@angular/forms';
import { timer, Subject } from 'rxjs';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';

class SiNumberInputComponent {
    /** The min. value for HTML input */
    min;
    /** The max. value for HTML input */
    max;
    /** The step size for HTML input */
    step = 1;
    /** The value */
    value;
    /** Optional unit label */
    unit;
    /** Show increment/decrement buttons? */
    showButtons = true;
    /** The aria-label passed to the input */
    ariaLabel;
    /** ID that is set on the input, e.g. for `<label for="...">` */
    inputId;
    get disabled() {
        return this._disabled;
    }
    /** @defaultref {@link _disabled} */
    set disabled(value) {
        this.setDisabledState(value !== false);
    }
    get readonly() {
        return this._readonly;
    }
    /** @defaultref {@link _readonly} */
    set readonly(value) {
        this._readonly = value !== false;
    }
    get required() {
        return this._required;
    }
    /** @defaultref {@link _required} */
    set required(value) {
        this._required = value !== false;
    }
    valueChange = new EventEmitter();
    internalValue;
    canInc = true;
    canDec = true;
    onTouched = () => { };
    onChange = () => { };
    ngModel;
    _disabled = false;
    _readonly = false;
    _required = false;
    autoUpdate$ = timer(400, 80);
    autoUpdateSubs;
    changeDetectorRef = inject(ChangeDetectorRef);
    ngOnChanges(changes) {
        if (changes.value) {
            this.internalValue = this.value;
        }
        this.modelChanged(this.internalValue, false);
    }
    /** @internal */
    registerOnChange(fn) {
        this.onChange = fn;
    }
    /** @internal */
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    /** @internal */
    setDisabledState(isDisabled) {
        this._disabled = isDisabled;
    }
    /** @internal */
    writeValue(value) {
        this.internalValue = value;
        this.changeDetectorRef.markForCheck();
    }
    /** @internal */
    validate(control) {
        const subject = new Subject();
        queueMicrotask(() => {
            subject.next(this.ngModel.control.valid ? null : this.ngModel.control.errors);
            subject.complete();
        });
        return subject;
    }
    modelChanged(value, emit = true) {
        this.onChange(value);
        if (emit) {
            this.valueChange.emit(value);
        }
        this.canInc =
            this.max == null || this.internalValue == null || this.internalValue + this.step <= this.max;
        this.canDec =
            this.min == null || this.internalValue == null || this.internalValue - this.step >= this.min;
    }
    autoUpdateStart(event, isIncrement) {
        const mouseButton = event.button;
        if (mouseButton) {
            return;
        }
        this.onTouched();
        event.preventDefault();
        const trigger = isIncrement ? () => this.increment() : () => this.decrement();
        this.autoUpdateSubs?.unsubscribe();
        this.autoUpdateSubs = this.autoUpdate$.subscribe(trigger);
        trigger();
    }
    autoUpdateStop() {
        this.autoUpdateSubs?.unsubscribe();
        this.autoUpdateSubs = undefined;
    }
    roundToStepPrecision(value) {
        const factor = 1 / this.step;
        if (factor > 1) {
            return Math.round(value * factor) / factor;
        }
        return Math.round(value / this.step) * this.step;
    }
    decrement() {
        this.internalValue =
            this.internalValue == null
                ? this.max ?? this.min ?? 0
                : this.roundToStepPrecision(this.internalValue - this.step);
        this.modelChanged(this.internalValue);
        if (!this.canDec) {
            this.autoUpdateStop();
        }
    }
    increment() {
        this.internalValue =
            this.internalValue == null
                ? this.min ?? this.max ?? 0
                : this.roundToStepPrecision(this.internalValue + this.step);
        this.modelChanged(this.internalValue);
        if (!this.canInc) {
            this.autoUpdateStop();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNumberInputComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiNumberInputComponent, selector: "si-number-input", inputs: { min: "min", max: "max", step: "step", value: "value", unit: "unit", showButtons: "showButtons", ariaLabel: ["aria-label", "ariaLabel"], inputId: "inputId", disabled: "disabled", readonly: "readonly", required: "required" }, outputs: { valueChange: "valueChange" }, host: { properties: { "class.disabled": "this._disabled", "class.readonly": "this._readonly" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: SiNumberInputComponent,
                multi: true
            },
            {
                provide: NG_ASYNC_VALIDATORS,
                useExisting: SiNumberInputComponent,
                multi: true
            }
        ], viewQueries: [{ propertyName: "ngModel", first: true, predicate: ["ctrl"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<input\n  #ctrl=\"ngModel\"\n  type=\"number\"\n  class=\"focus-none\"\n  [attr.id]=\"inputId ?? null\"\n  [ngModelOptions]=\"{ standalone: true }\"\n  [min]=\"min ?? null\"\n  [max]=\"max ?? null\"\n  [step]=\"step\"\n  [required]=\"required\"\n  [readonly]=\"readonly\"\n  [disabled]=\"disabled\"\n  [attr.aria-label]=\"ariaLabel\"\n  [(ngModel)]=\"internalValue\"\n  (blur)=\"onTouched()\"\n  (ngModelChange)=\"modelChanged($event)\"\n/>\n<span\n  *ngIf=\"unit\"\n  class=\"unit ms-2\"\n  [class.text-secondary]=\"!disabled\"\n  [class.text-disabled]=\"disabled\"\n  >{{ unit }}</span\n>\n<ng-container *ngIf=\"showButtons\">\n  <button\n    type=\"button\"\n    tabindex=\"-1\"\n    aria-hidden=\"true\"\n    class=\"dec btn btn-circle btn-tertiary btn-xs element-minus my-n3 ms-2\"\n    [disabled]=\"disabled || readonly || !canDec\"\n    (mousedown)=\"autoUpdateStart($event, false)\"\n    (touchstart)=\"autoUpdateStart($event, false)\"\n    (touchend)=\"autoUpdateStop()\"\n    (mouseup)=\"autoUpdateStop()\"\n    (mouseleave)=\"autoUpdateStop()\"\n  >\n  </button>\n  <button\n    type=\"button\"\n    tabindex=\"-1\"\n    aria-hidden=\"true\"\n    class=\"inc btn btn-circle btn-tertiary btn-xs element-plus my-n3 me-n2\"\n    [disabled]=\"disabled || readonly || !canInc\"\n    (mousedown)=\"autoUpdateStart($event, true)\"\n    (touchstart)=\"autoUpdateStart($event, true)\"\n    (touchend)=\"autoUpdateStop()\"\n    (mouseup)=\"autoUpdateStop()\"\n    (mouseleave)=\"autoUpdateStop()\"\n  >\n  </button>\n</ng-container>\n", styles: [":host{display:flex;align-items:center}:host:focus-within{box-shadow:0 0 0 var(--element-button-focus-overlay-width) var(--element-button-focus-overlay-color),0 0 0 calc(var(--element-button-focus-width) + var(--element-button-focus-overlay-width)) var(--element-focus-default)}input{-moz-appearance:textfield;border:0;margin:0;padding-block:0;outline:none;background:transparent;color:inherit;text-align:inherit;flex:1 0 0;min-inline-size:0}input::-webkit-outer-spin-button,input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NumberValueAccessor, selector: "input[type=number][formControlName],input[type=number][formControl],input[type=number][ngModel]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.MinValidator, selector: "input[type=number][min][formControlName],input[type=number][min][formControl],input[type=number][min][ngModel]", inputs: ["min"] }, { kind: "directive", type: i2.MaxValidator, selector: "input[type=number][max][formControlName],input[type=number][max][formControl],input[type=number][max][ngModel]", inputs: ["max"] }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNumberInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-number-input', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: SiNumberInputComponent,
                            multi: true
                        },
                        {
                            provide: NG_ASYNC_VALIDATORS,
                            useExisting: SiNumberInputComponent,
                            multi: true
                        }
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<input\n  #ctrl=\"ngModel\"\n  type=\"number\"\n  class=\"focus-none\"\n  [attr.id]=\"inputId ?? null\"\n  [ngModelOptions]=\"{ standalone: true }\"\n  [min]=\"min ?? null\"\n  [max]=\"max ?? null\"\n  [step]=\"step\"\n  [required]=\"required\"\n  [readonly]=\"readonly\"\n  [disabled]=\"disabled\"\n  [attr.aria-label]=\"ariaLabel\"\n  [(ngModel)]=\"internalValue\"\n  (blur)=\"onTouched()\"\n  (ngModelChange)=\"modelChanged($event)\"\n/>\n<span\n  *ngIf=\"unit\"\n  class=\"unit ms-2\"\n  [class.text-secondary]=\"!disabled\"\n  [class.text-disabled]=\"disabled\"\n  >{{ unit }}</span\n>\n<ng-container *ngIf=\"showButtons\">\n  <button\n    type=\"button\"\n    tabindex=\"-1\"\n    aria-hidden=\"true\"\n    class=\"dec btn btn-circle btn-tertiary btn-xs element-minus my-n3 ms-2\"\n    [disabled]=\"disabled || readonly || !canDec\"\n    (mousedown)=\"autoUpdateStart($event, false)\"\n    (touchstart)=\"autoUpdateStart($event, false)\"\n    (touchend)=\"autoUpdateStop()\"\n    (mouseup)=\"autoUpdateStop()\"\n    (mouseleave)=\"autoUpdateStop()\"\n  >\n  </button>\n  <button\n    type=\"button\"\n    tabindex=\"-1\"\n    aria-hidden=\"true\"\n    class=\"inc btn btn-circle btn-tertiary btn-xs element-plus my-n3 me-n2\"\n    [disabled]=\"disabled || readonly || !canInc\"\n    (mousedown)=\"autoUpdateStart($event, true)\"\n    (touchstart)=\"autoUpdateStart($event, true)\"\n    (touchend)=\"autoUpdateStop()\"\n    (mouseup)=\"autoUpdateStop()\"\n    (mouseleave)=\"autoUpdateStop()\"\n  >\n  </button>\n</ng-container>\n", styles: [":host{display:flex;align-items:center}:host:focus-within{box-shadow:0 0 0 var(--element-button-focus-overlay-width) var(--element-button-focus-overlay-color),0 0 0 calc(var(--element-button-focus-width) + var(--element-button-focus-overlay-width)) var(--element-focus-default)}input{-moz-appearance:textfield;border:0;margin:0;padding-block:0;outline:none;background:transparent;color:inherit;text-align:inherit;flex:1 0 0;min-inline-size:0}input::-webkit-outer-spin-button,input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}\n"] }]
        }], propDecorators: { min: [{
                type: Input
            }], max: [{
                type: Input
            }], step: [{
                type: Input
            }], value: [{
                type: Input
            }], unit: [{
                type: Input
            }], showButtons: [{
                type: Input
            }], ariaLabel: [{
                type: Input,
                args: ['aria-label']
            }], inputId: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], required: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], ngModel: [{
                type: ViewChild,
                args: ['ctrl', { static: true }]
            }], _disabled: [{
                type: HostBinding,
                args: ['class.disabled']
            }], _readonly: [{
                type: HostBinding,
                args: ['class.readonly']
            }] } });

class SiNumberInputModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNumberInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiNumberInputModule, declarations: [SiNumberInputComponent], imports: [CommonModule, FormsModule], exports: [SiNumberInputComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNumberInputModule, imports: [CommonModule, FormsModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNumberInputModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule],
                    declarations: [SiNumberInputComponent],
                    exports: [SiNumberInputComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiNumberInputComponent, SiNumberInputModule };
